import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useAdminContext } from '../contexts/AdminContextProvider';
import TenantContextDropdown from '../components/common/TenantContextDropdown';
import {
  SearchIcon,
  PlusIcon,
  EditIcon,
  TrashIcon,
  RefreshIcon,
  UsersIcon,
  CheckCircleIcon,
  AlertIcon,
  BuildingIcon
} from '../components/common/Icons';

// Common user interface that works for both platform and tenant users
interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  status: string;
  is_active?: boolean; // Platform users use this
  email_verified?: boolean;
  created_at: string;
  last_login?: string | null; // Platform users
  last_login_at?: string | null; // Tenant users
  tenant_id?: string; // Tenant users only
}

// Available role from API
interface RoleOption {
  id: string;
  display_name: string;
}

const AdminUsersPage: React.FC = () => {
  const { authFetch, isLoading: authLoading } = useAuth();
  const { activeTenant, isInTenantContext } = useAdminContext();
  const [users, setUsers] = useState<User[]>([]);
  const [availableRoles, setAvailableRoles] = useState<RoleOption[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRole, setSelectedRole] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editForm, setEditForm] = useState({
    email: '',
    name: '',
    role: '',
    status: 'active'
  });

  // New user form state
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    name: '',
    role: 'user'
  });

  // Confirmation Dialog state
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmDialogConfig, setConfirmDialogConfig] = useState<{
    title: string;
    message: string;
    confirmText: string;
    confirmVariant: 'danger' | 'primary';
    onConfirm: () => void;
  } | null>(null);

  // Error/Info Dialog state
  const [showInfoDialog, setShowInfoDialog] = useState(false);
  const [infoDialogConfig, setInfoDialogConfig] = useState<{
    title: string;
    message: string;
    variant: 'error' | 'success' | 'info';
  } | null>(null);

  // Get the appropriate API base path based on context
  const getApiBasePath = useCallback(() => {
    if (isInTenantContext && activeTenant) {
      return `/api/admin/tenants/${activeTenant.id}/users`;
    }
    return '/api/admin/users';
  }, [isInTenantContext, activeTenant]);

  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const apiPath = getApiBasePath();
      console.log('[AdminUsersPage] Fetching users from:', apiPath, {
        isInTenantContext,
        activeTenantId: activeTenant?.id,
        activeTenantName: activeTenant?.name,
      });
      const response = await authFetch(apiPath);
      if (response.ok) {
        const data = await response.json();
        console.log('[AdminUsersPage] Users response:', data);
        // Handle both wrapped and unwrapped response formats
        const userList = data.users || data;
        // Normalize users to have consistent is_active field
        const normalizedUsers = (Array.isArray(userList) ? userList : []).map((user: User) => ({
          ...user,
          // Tenant users use status field, platform users use is_active
          is_active: user.is_active !== undefined
            ? user.is_active
            : user.status === 'active',
        }));
        setUsers(normalizedUsers);
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('[AdminUsersPage] API error:', response.status, errorData);
        setError(errorData.detail || `Failed to fetch users: ${response.status}`);
      }
    } catch (err) {
      console.error('[AdminUsersPage] Failed to fetch users:', err);
      setError('Failed to connect to API');
    }
    setIsLoading(false);
  }, [authFetch, getApiBasePath, isInTenantContext, activeTenant]);

  // Fetch available roles from API
  const fetchRoles = useCallback(async () => {
    try {
      const response = await authFetch('/api/admin/roles');
      if (response.ok) {
        const roles = await response.json();
        // Filter out superadmin from assignable roles (can't be assigned)
        const assignableRoles = roles
          .filter((r: { id: string }) => r.id !== 'superadmin')
          .map((r: { id: string; display_name: string }) => ({
            id: r.id,
            display_name: r.display_name
          }));
        setAvailableRoles(assignableRoles);
      }
    } catch (err) {
      console.error('[AdminUsersPage] Failed to fetch roles:', err);
    }
  }, [authFetch]);

  // Refetch when context changes
  useEffect(() => {
    if (authLoading) return;
    fetchUsers();
    fetchRoles();
  }, [authLoading, fetchUsers, fetchRoles]);

  const handleCreateUser = async () => {
    try {
      const apiPath = getApiBasePath();
      const response = await authFetch(apiPath, {
        method: 'POST',
        body: JSON.stringify(newUser)
      });
      if (response.ok) {
        setShowAddModal(false);
        setNewUser({ email: '', password: '', name: '', role: 'user' });
        fetchUsers();
      } else {
        const errorData = await response.json().catch(() => ({}));
        // Handle various error formats
        let errorMessage = 'Failed to create user';
        if (errorData.error?.message) {
          // Format: { error: { message: "..." } }
          errorMessage = errorData.error.message;
        } else if (typeof errorData.detail === 'object') {
          // Format: { detail: { message: "...", errors: [...] } }
          errorMessage = errorData.detail.message || JSON.stringify(errorData.detail);
          if (errorData.detail.errors && Array.isArray(errorData.detail.errors)) {
            errorMessage += '\n\n' + errorData.detail.errors.join('\n');
          }
        } else if (errorData.detail) {
          // Format: { detail: "..." }
          errorMessage = errorData.detail;
        } else if (errorData.message) {
          // Format: { message: "..." }
          errorMessage = errorData.message;
        }
        setInfoDialogConfig({
          title: 'Error Creating User',
          message: errorMessage,
          variant: 'error',
        });
        setShowInfoDialog(true);
      }
    } catch (err) {
      console.error('[AdminUsersPage] Create user error:', err);
      setInfoDialogConfig({
        title: 'Network Error',
        message: 'Failed to create user. Please check your connection and try again.',
        variant: 'error',
      });
      setShowInfoDialog(true);
    }
  };

  // Show confirmation dialog for user deletion
  const confirmDeleteUser = (user: User) => {
    setConfirmDialogConfig({
      title: 'Delete User',
      message: `Are you sure you want to delete "${user.email}"? This action cannot be undone.`,
      confirmText: 'Delete User',
      confirmVariant: 'danger',
      onConfirm: () => executeDeleteUser(user.id),
    });
    setShowConfirmDialog(true);
  };

  // Execute the actual user deletion
  const executeDeleteUser = async (userId: string) => {
    setShowConfirmDialog(false);
    setConfirmDialogConfig(null);

    try {
      const apiPath = `${getApiBasePath()}/${userId}`;
      const response = await authFetch(apiPath, {
        method: 'DELETE'
      });
      if (response.ok || response.status === 204) {
        fetchUsers();
      } else {
        const error = await response.json();
        setInfoDialogConfig({
          title: 'Error Deleting User',
          message: error.detail || 'Failed to delete user',
          variant: 'error',
        });
        setShowInfoDialog(true);
      }
    } catch (err) {
      setInfoDialogConfig({
        title: 'Network Error',
        message: 'Failed to delete user. Please check your connection and try again.',
        variant: 'error',
      });
      setShowInfoDialog(true);
    }
  };

  // Legacy handler for backward compatibility with table row onClick
  const handleDeleteUser = (userId: string) => {
    const user = users.find(u => u.id === userId);
    if (user) {
      confirmDeleteUser(user);
    }
  };

  const handleToggleStatus = async (userId: string, isActive: boolean) => {
    try {
      // Platform uses PUT /users/{id}/status, tenant uses PUT /tenants/{id}/users/{id}
      const newStatus = isActive ? 'suspended' : 'active';
      let apiPath: string;
      let body: object;

      if (isInTenantContext && activeTenant) {
        // Tenant API: PUT /api/admin/tenants/{tenant_id}/users/{user_id}
        apiPath = `${getApiBasePath()}/${userId}`;
        body = { status: newStatus };
      } else {
        // Platform API: PUT /api/admin/users/{user_id}/status
        apiPath = `/api/admin/users/${userId}/status`;
        body = { status: newStatus };
      }

      const response = await authFetch(apiPath, {
        method: 'PUT',
        body: JSON.stringify(body)
      });
      if (response.ok) {
        fetchUsers();
      } else {
        const error = await response.json();
        setInfoDialogConfig({
          title: 'Error Updating Status',
          message: error.detail || 'Failed to update user status',
          variant: 'error',
        });
        setShowInfoDialog(true);
      }
    } catch (err) {
      setInfoDialogConfig({
        title: 'Network Error',
        message: 'Failed to update user status. Please check your connection and try again.',
        variant: 'error',
      });
      setShowInfoDialog(true);
    }
  };

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setEditForm({
      email: user.email,
      name: user.name || '',
      role: user.role,
      status: user.is_active ? 'active' : (user.status || 'suspended')
    });
    setShowEditModal(true);
  };

  const handleUpdateUser = async () => {
    if (!editingUser) return;

    try {
      const apiPath = `${getApiBasePath()}/${editingUser.id}`;
      const response = await authFetch(apiPath, {
        method: 'PUT',
        body: JSON.stringify({
          email: editForm.email,
          name: editForm.name || null,
          role: editForm.role,
          status: editForm.status
        })
      });

      if (response.ok) {
        setShowEditModal(false);
        setEditingUser(null);
        fetchUsers();
      } else {
        const error = await response.json();
        const errorMessage = typeof error.detail === 'object'
          ? error.detail.message || JSON.stringify(error.detail)
          : error.detail || 'Failed to update user';
        setInfoDialogConfig({
          title: 'Error Updating User',
          message: errorMessage,
          variant: 'error',
        });
        setShowInfoDialog(true);
      }
    } catch (err) {
      setInfoDialogConfig({
        title: 'Network Error',
        message: 'Failed to update user. Please check your connection and try again.',
        variant: 'error',
      });
      setShowInfoDialog(true);
    }
  };

  // Get unique roles from users
  const roles = ['all', ...new Set(users.map(u => u.role))];

  // Filter users
  const filteredUsers = users.filter(user => {
    const matchesSearch =
      (user.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (user.email || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = selectedRole === 'all' || user.role === selectedRole;
    const matchesStatus = selectedStatus === 'all' ||
      (selectedStatus === 'active' && user.is_active) ||
      (selectedStatus === 'inactive' && !user.is_active);
    return matchesSearch && matchesRole && matchesStatus;
  });

  const getStatusBadge = (isActive: boolean) => {
    if (isActive) {
      return <span className="badge badge-success"><CheckCircleIcon size={14} /> Active</span>;
    }
    return <span className="badge badge-secondary">Inactive</span>;
  };

  // Context-aware labels
  const pageTitle = isInTenantContext ? 'Tenant User Management' : 'User Management';
  const pageSubtitle = isInTenantContext && activeTenant
    ? `Manage users for ${activeTenant.name} (${users.length} total)`
    : `Manage platform users (${users.length} total)`;

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="page-header-left">
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <h1 className="page-title">{pageTitle}</h1>
            <TenantContextDropdown />
            {/* Context Indicator Badge */}
            {isInTenantContext && activeTenant ? (
              <span
                className="badge"
                style={{
                  background: activeTenant.branding?.primaryColor || 'var(--primary)',
                  color: '#fff',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '4px 10px',
                  fontSize: '12px',
                  fontWeight: 500,
                }}
              >
                <BuildingIcon size={12} />
                {activeTenant.name}
              </span>
            ) : (
              <span
                className="badge"
                style={{
                  background: 'rgba(59, 130, 246, 0.15)',
                  color: '#3b82f6',
                  border: '1px solid rgba(59, 130, 246, 0.3)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '4px 10px',
                  fontSize: '12px',
                  fontWeight: 500,
                }}
              >
                Platform Level
              </span>
            )}
          </div>
          <p className="page-subtitle">{pageSubtitle}</p>
        </div>
        <div className="page-header-right">
          <button className="btn btn-primary" onClick={() => setShowAddModal(true)}>
            <PlusIcon size={16} />
            Add User
          </button>
          <button className="btn btn-ghost" onClick={fetchUsers}>
            <RefreshIcon size={18} />
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="page-toolbar">
        <div className="input-wrapper" style={{ maxWidth: '320px' }}>
          <SearchIcon size={18} className="input-icon" />
          <input
            type="text"
            className="input input-with-icon"
            placeholder="Search users..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <select
          className="select"
          value={selectedRole}
          onChange={(e) => setSelectedRole(e.target.value)}
        >
          {roles.map(role => (
            <option key={role} value={role}>
              {role === 'all' ? 'All Roles' : role}
            </option>
          ))}
        </select>
        <select
          className="select"
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      {/* Users Table */}
      <div className="glass-card">
        {isLoading ? (
          <div className="loading-state">
            <div className="spinner" />
            <p>Loading users...</p>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="empty-state">
            <UsersIcon size={48} />
            <p>No users found</p>
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Role</th>
                <th>Status</th>
                <th>Created</th>
                <th>Last Login</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => (
                <tr key={user.id}>
                  <td>
                    <div className="flex items-center gap-3">
                      <div className="avatar">
                        {(user.name || user.email || '?').charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="font-medium">{user.name || 'No name'}</div>
                        <div className="text-secondary text-sm">{user.email}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className="badge badge-info">{user.role}</span>
                  </td>
                  <td>{getStatusBadge(user.is_active ?? false)}</td>
                  <td className="text-secondary text-sm">
                    {user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}
                  </td>
                  <td className="text-secondary text-sm">
                    {(user.last_login || user.last_login_at)
                      ? new Date(user.last_login || user.last_login_at || '').toLocaleDateString()
                      : 'Never'}
                  </td>
                  <td>
                    <div className="flex gap-2">
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => handleEditUser(user)}
                        title="Edit user"
                      >
                        <EditIcon size={14} />
                      </button>
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => handleToggleStatus(user.id, user.is_active ?? false)}
                        title={(user.is_active ?? false) ? 'Suspend user' : 'Activate user'}
                      >
                        {(user.is_active ?? false) ? <AlertIcon size={14} /> : <CheckCircleIcon size={14} />}
                      </button>
                      <button
                        className="btn btn-ghost btn-sm text-error"
                        onClick={() => handleDeleteUser(user.id)}
                        title="Delete user"
                      >
                        <TrashIcon size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Add User Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {isInTenantContext && activeTenant
                  ? `Add User to ${activeTenant.name}`
                  : 'Add New Platform User'}
              </h2>
              <button className="btn btn-ghost" onClick={() => setShowAddModal(false)}>×</button>
            </div>
            {/* Context indicator in modal */}
            {isInTenantContext && activeTenant && (
              <div
                style={{
                  padding: '8px 16px',
                  background: `${activeTenant.branding?.primaryColor || 'var(--primary)'}15`,
                  borderBottom: '1px solid var(--border)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '13px',
                }}
              >
                <BuildingIcon size={14} />
                <span>Creating user in tenant: <strong>{activeTenant.name}</strong></span>
              </div>
            )}
            <div className="modal-body">
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  className="input"
                  placeholder="user@example.com"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Name</label>
                <input
                  type="text"
                  className="input"
                  placeholder="John Doe"
                  value={newUser.name}
                  onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Password</label>
                <input
                  type="password"
                  className="input"
                  placeholder="Minimum 12 characters"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Role</label>
                <select
                  className="select"
                  value={newUser.role}
                  onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                >
                  {availableRoles.map(role => (
                    <option key={role.id} value={role.id}>
                      {role.display_name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group" style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                <p>Password requirements: minimum 12 characters, at least one uppercase, lowercase, number, and special character.</p>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowAddModal(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleCreateUser}>
                Create User
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && editingUser && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit User</h2>
              <button className="btn btn-ghost" onClick={() => setShowEditModal(false)}>×</button>
            </div>
            {/* Context indicator in modal */}
            {isInTenantContext && activeTenant && (
              <div
                style={{
                  padding: '8px 16px',
                  background: `${activeTenant.branding?.primaryColor || 'var(--primary)'}15`,
                  borderBottom: '1px solid var(--border)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '13px',
                }}
              >
                <BuildingIcon size={14} />
                <span>Editing user in tenant: <strong>{activeTenant.name}</strong></span>
              </div>
            )}
            <div className="modal-body">
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  className="input"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Name</label>
                <input
                  type="text"
                  className="input"
                  placeholder="Optional"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Role</label>
                <select
                  className="select"
                  value={editForm.role}
                  onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                >
                  {/* Always include current role first */}
                  {!availableRoles.find(r => r.id === editingUser.role) && (
                    <option value={editingUser.role}>{editingUser.role}</option>
                  )}
                  {availableRoles.map(role => (
                    <option key={role.id} value={role.id}>
                      {role.display_name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Status</label>
                <select
                  className="select"
                  value={editForm.status}
                  onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                >
                  <option value="active">Active</option>
                  <option value="suspended">Suspended</option>
                  <option value="pending">Pending</option>
                  <option value="deactivated">Deactivated</option>
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowEditModal(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleUpdateUser}>
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      {showConfirmDialog && confirmDialogConfig && (
        <div className="modal-overlay" onClick={() => setShowConfirmDialog(false)}>
          <div
            className="modal"
            style={{ maxWidth: '420px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2 style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <AlertIcon size={20} style={{ color: 'var(--error)' }} />
                {confirmDialogConfig.title}
              </h2>
              <button className="btn btn-ghost" onClick={() => setShowConfirmDialog(false)}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                {confirmDialogConfig.message}
              </p>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowConfirmDialog(false)}
              >
                Cancel
              </button>
              <button
                className={`btn ${confirmDialogConfig.confirmVariant === 'danger' ? 'btn-error' : 'btn-primary'}`}
                onClick={confirmDialogConfig.onConfirm}
              >
                {confirmDialogConfig.confirmText}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Info/Error Dialog */}
      {showInfoDialog && infoDialogConfig && (
        <div className="modal-overlay" onClick={() => setShowInfoDialog(false)}>
          <div
            className="modal"
            style={{ maxWidth: '420px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="modal-header"
              style={infoDialogConfig.variant === 'error' ? {
                borderBottom: '1px solid rgba(239, 68, 68, 0.2)',
                background: 'rgba(239, 68, 68, 0.05)'
              } : {}}
            >
              <h2 style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                color: infoDialogConfig.variant === 'error' ? 'var(--error)' : 'inherit'
              }}>
                <AlertIcon size={20} />
                {infoDialogConfig.title}
              </h2>
              <button className="btn btn-ghost" onClick={() => setShowInfoDialog(false)}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ lineHeight: '1.6' }}>
                {infoDialogConfig.message}
              </p>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-primary"
                onClick={() => setShowInfoDialog(false)}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsersPage;
